/**
 * core/router.ts · Hash-based 路由 (版本见 src/constants/company.ts APP_VERSION)
 *
 * 设计原则:
 *   - hash 路由 · 老 #salesOrderList 等全兼容
 *   - Page module lazy load (Vite import() 自动 code split)
 *   - 唯一 mountPoint #app
 *   - 未登录 → 强制 #login
 *   - 路由前 RBAC 检查
 *   - 仓储子角色智能跳转 (WH-02 → warehouseRawHome / WH-03 → warehouseFinishedHome)
 *   - 路由 hash 用 Routes 常量 (constants/routes.ts 集中管理)
 */

import { getCurrentUser } from './auth';
import { canAccess } from './rbac';
import { renderTopNav } from '@widgets/TopNav';
import { renderSidebar } from '@widgets/Sidebar';
import { getHomeByUserCode } from '@constants/role';
import { Routes } from '@constants/routes';

export type PageRenderer = () => Promise<HTMLElement> | HTMLElement;

/**
 * 路由表 · 按部门组织 · 100+ 路由 · 集中单一来源
 */
const routes: Record<string, () => Promise<{ render: PageRenderer }>> = {
  // ═══════════ 系统 ═══════════
  [Routes.LOGIN]: () => import('@pages/auth/Login'),

  // ═══════════ 13 *Home (含仓储 3 子部门) ═══════════
  [Routes.GM_HOME]:                 () => import('@pages/home/GmHome'),
  [Routes.SALES_HOME]:              () => import('@pages/home/SalesHome'),
  [Routes.ENGINEER_HOME]:           () => import('@pages/home/EngineerHome'),
  [Routes.PURCHASING_HOME]:         () => import('@pages/home/PurchasingHome'),
  [Routes.WAREHOUSE_HOME]:          () => import('@pages/home/WarehouseHome'),
  [Routes.WAREHOUSE_RAW_HOME]:      () => import('@pages/home/WarehouseRawHome'),      // ⭐ WH-02/04
  [Routes.WAREHOUSE_FINISHED_HOME]: () => import('@pages/home/WarehouseFinishedHome'), // ⭐ WH-03/05
  [Routes.PRODUCTION_HOME]:         () => import('@pages/home/SupervisorHome'),
  [Routes.WORKER_HOME]:             () => import('@pages/home/WorkerHome'),
  [Routes.QUALITY_HOME]:            () => import('@pages/home/QualityHome'),
  [Routes.MAINT_HOME]:              () => import('@pages/home/MaintHome'),
  [Routes.FINANCE_HOME]:            () => import('@pages/home/FinanceHome'),
  [Routes.HR_HOME]:                 () => import('@pages/home/HrHome'),
  [Routes.GUARD_HOME]:              () => import('@pages/home/GuardHome'),
  [Routes.ISO_HOME]:                () => import('@pages/home/IsoHome'),

  // ═══════════ 销售部 ═══════════
  [Routes.QUOTATION_LIST]:    () => import('@pages/sales/QuotationList'),
  [Routes.QUOTATION_CREATE]:  () => import('@pages/sales/QuotationCreate'),
  [Routes.SALES_ORDER_LIST]:  () => import('@pages/sales/SalesOrderList'),
  [Routes.SALES_ORDER_CREATE]:() => import('@pages/sales/SalesOrderCreate'),
  [Routes.ORDER_360]:         () => import('@pages/sales/Order360'),
  [Routes.CUSTOMER_LIST]:     () => import('@pages/sales/CustomerList'),
  [Routes.CUSTOMER_CREATE]:   () => import('@pages/sales/CustomerCreate'),
  [Routes.CUSTOMER_360]:      () => import('@pages/sales/Customer360'),
  [Routes.SALES_PIPELINE]:    () => import('@pages/sales/SalesPipeline'),
  [Routes.SALES_ANALYSIS]:    () => import('@pages/sales/SalesAnalysis'),

  // ═══════════ 工程部 ═══════════
  [Routes.PRODUCT_LIST]:      () => import('@pages/engineering/ProductList'),
  [Routes.PRODUCT_360]:       () => import('@pages/engineering/Product360'),

  // ═══════════ 采购部 ═══════════
  // ═══════════ 销售部 · v363 合同 ═══════════
  [Routes.CONTRACT_LIST]:     () => import('@pages/sales/ContractList'),
  [Routes.CONTRACT_CREATE]:   () => import('@pages/sales/ContractCreate'),
  [Routes.CONTRACT_DETAIL]:   () => import('@pages/sales/ContractDetail'),

  [Routes.PR_LIST]:           () => import('@pages/purchasing/PRList'),
  [Routes.PR_CREATE]:         () => import('@pages/purchasing/PRCreate'),
  [Routes.PR_DETAIL]:         () => import('@pages/purchasing/PRDetail'), // ⭐ Sprint 3 P0
  [Routes.PO_LIST]:           () => import('@pages/purchasing/POList'),
  [Routes.PO_CREATE]:         () => import('@pages/purchasing/POCreate'),
  [Routes.SUPPLIER_LIST]:     () => import('@pages/purchasing/SupplierList'),
  [Routes.SUPPLIER_360]:      () => import('@pages/purchasing/Supplier360'),

  // ═══════════ 仓储原料仓 ⭐ ═══════════
  [Routes.RECEIVE_MATERIAL]:  () => import('@pages/warehouse/raw/ReceiveMaterial'),
  [Routes.RECEIPT_QR]:        () => import('@pages/warehouse/raw/ReceiptQR'),
  [Routes.ISSUE_MATERIAL]:    () => import('@pages/warehouse/raw/IssueMaterial'),

  // ═══════════ 仓储成品仓 ⭐ ═══════════
  [Routes.FINISHED_PRODUCT_LIST]: () => import('@pages/warehouse/finished/FinishedProductList'),
  [Routes.INVENTORY_LIST]:    () => import('@pages/warehouse/finished/FinishedProductList'), // 兼容老 #inventoryList
  [Routes.SHIPPING_NOTE]:     () => import('@pages/warehouse/finished/ShippingNote'), // ⭐ Sprint 4 P1
  [Routes.SHIPPING_NOTE_DETAIL]: () => import('@pages/warehouse/finished/ShippingNoteDetail'), // ⭐ v362 J-2 (送货单详情 + 🖨️)

  // ═══════════ 生产部 ═══════════
  [Routes.SHORTAGE_LIST]:     () => import('@pages/production/ShortageList'), // ⭐ v364 I-C 缺数+退货+补数
  [Routes.WO_MANAGE]:         () => import('@pages/production/WOManage'),
  [Routes.WO_CREATE]:         () => import('@pages/production/WOCreate'), // ⭐ Sprint 3 P0
  [Routes.WO_DETAIL]:         () => import('@pages/production/WODetail'), // ⭐ Sprint 3 P0
  [Routes.SCAN_START]:        () => import('@pages/production/ScanStart'),
  [Routes.SCAN_REPORT]:       () => import('@pages/production/ScanReport'),
  [Routes.GANTT_SCHEDULE]:    () => import('@pages/production/GanttSchedule'),
  [Routes.PRODUCTION_KANBAN]: () => import('@pages/production/ProductionKanban'),
  [Routes.WORKSHOP_SCREEN]:   () => import('@pages/production/WorkshopScreen'),
  bigScreenProduction:        () => import('@pages/production/BigScreenProduction'),  // ⭐ v357
  bigScreenData:              () => import('@pages/production/BigScreenData'),         // ⭐ v357
  [Routes.EQUIPMENT_LIST]:    () => import('@pages/production/EquipmentList'),

  // ═══════════ 品控部 ═══════════
  [Routes.QC_RECORDS]:        () => import('@pages/quality/QCRecords'),
  [Routes.QC_DETAIL]:         () => import('@pages/quality/QCDetail'), // ⭐ v362 J-2 (QC 报告详情 + 🖨️)
  [Routes.IQC_INCOMING]:      () => import('@pages/quality/IQCIncoming'),
  [Routes.IPQC_INLINE]:       () => import('@pages/quality/IPQCInline'),
  [Routes.OQC_OUTGOING]:      () => import('@pages/quality/OQCOutgoing'),
  [Routes.QUALITY_GATE]:      () => import('@pages/quality/QualityGate'),

  // ═══════════ 财务部 ═══════════
  [Routes.AR_INVOICE_LIST]:   () => import('@pages/finance/ARInvoiceList'),
  [Routes.AR_INVOICE_CREATE]: () => import('@pages/finance/ARInvoiceCreate'), // ⭐ Sprint 3 P0
  [Routes.AR_AGING]:          () => import('@pages/finance/ARAging'),
  [Routes.AP_INVOICE_LIST]:   () => import('@pages/finance/APInvoiceList'),  // ⭐ v324 Sprint 8-G
  [Routes.AP_INVOICE_DETAIL]: () => import('@pages/finance/APInvoiceDetail'), // ⭐ v362 J-2 (AP 存档 + 🖨️)
  [Routes.AP_AGING]:          () => import('@pages/finance/APAging'),
  [Routes.CUSTOMER_PAYMENTS]: () => import('@pages/finance/CustomerPayments'),
  [Routes.CUSTOMER_PAYMENT_CREATE]: () => import('@pages/finance/CustomerPaymentCreate'),
  [Routes.PAYMENT_DETAIL]:    () => import('@pages/finance/PaymentDetail'), // ⭐ v362 J-2 (收款凭证 + 🖨️)
  [Routes.PROFIT_LOSS]:       () => import('@pages/finance/ProfitLoss'),
  [Routes.ORDER_PNL]:         () => import('@pages/finance/OrderPnl'),       // ⭐ v365 I-D 订单损益
  [Routes.ORDER_PNL_REPORT]:  () => import('@pages/finance/OrderPnlReport'),  // ⭐ v365 I-D 期间报告
  [Routes.MONTHLY_OVERHEAD]:  () => import('@pages/finance/MonthlyOverhead'),

  // ═══════════ 人事部 ═══════════
  [Routes.EMPLOYEE_MANAGE]:   () => import('@pages/hr/EmployeeManage'),
  [Routes.EMPLOYEE_CREATE]:   () => import('@pages/hr/EmployeeCreate'),
  [Routes.HR_TIME_CENTER]:    () => import('@pages/hr/HRTimeCenter'),
  [Routes.PAYROLL_CALC]:      () => import('@pages/hr/PayrollCalc'),
  [Routes.LEAVE_REQUEST]:     () => import('@pages/hr/LeaveRequestList'),
  [Routes.OVERTIME_REQUEST]:  () => import('@pages/hr/OvertimeRequestList'),
  [Routes.TRAINING_RECORD]:   () => import('@pages/hr/TrainingRecordList'),

  // ═══════════ v340 P1+P2 新页 ═══════════
  leaveRequestCreate:         () => import('@pages/hr/LeaveRequestCreate'),

  // ═══════════ v335 大批量 36 新页 ═══════════
  // 财务深扩 (v330)
  apInvoiceCreate:            () => import('@pages/finance/APInvoiceCreate'),
  arInvoiceDetail:            () => import('@pages/finance/ARInvoiceDetail'),
  financePipeline:            () => import('@pages/finance/FinancePipeline'),
  voucherList:                () => import('@pages/finance/VoucherList'),
  voucherCreate:              () => import('@pages/finance/VoucherCreate'),
  bankAccounts:               () => import('@pages/finance/BankAccounts'),
  reconciliation:             () => import('@pages/finance/Reconciliation'),
  monthlyProfitReport:        () => import('@pages/finance/MonthlyProfitReport'),
  expenseHRReview:            () => import('@pages/finance/ExpenseHRReview'),
  expenseFinanceReview:       () => import('@pages/finance/ExpenseFinanceReview'),
  // 仓储深扩 (v331)
  rawMaterialQuery:           () => import('@pages/warehouse/raw/RawMaterialQuery'),
  mrpDashboard:               () => import('@pages/warehouse/raw/MrpDashboard'),
  inventoryAdjust:            () => import('@pages/warehouse/overview/InventoryAdjust'),
  inventoryTransfer:          () => import('@pages/warehouse/overview/InventoryTransfer'),
  logisticsCenter:            () => import('@pages/warehouse/finished/LogisticsCenter'),
  stockByWarehouse:           () => import('@pages/warehouse/overview/StockByWarehouse'),
  finishedProductInventory:   () => import('@pages/warehouse/finished/FinishedProductInventory'),
  // 工人移动端 (v332)
  myWages:                    () => import('@pages/hr/MyWages'),
  workerAttendance:           () => import('@pages/hr/WorkerAttendance'),
  workerRepair:               () => import('@pages/maintenance/WorkerRepair'),
  workerDashboard:            () => import('@pages/production/WorkerDashboard'),
  // Detail 6 页 (v333)
  salesOrderDetail:           () => import('@pages/sales/SalesOrderDetail'),
  quotationDetail:            () => import('@pages/sales/QuotationDetail'),
  poDetail:                   () => import('@pages/purchasing/PODetail'),
  employeeDetail:             () => import('@pages/hr/EmployeeDetail'),
  complaintDetail:            () => import('@pages/quality/ComplaintDetail'),
  // 保卫 6 页 (v334)
  guardScanIn:                () => import('@pages/guard/GuardScanIn'),
  guardScanOut:               () => import('@pages/guard/GuardScanOut'),
  guardHistory:               () => import('@pages/guard/GuardHistory'),
  guardPatrolDashboard:       () => import('@pages/guard/GuardPatrolDashboard'),
  guardPatrolScan:            () => import('@pages/guard/GuardPatrolScan'),
  guardGateControl:           () => import('@pages/guard/GuardGateControl'),
  // 生产 4 页 (v335)
  oeeCenter:                  () => import('@pages/production/OeeCenter'),
  woTracking:                 () => import('@pages/production/WOTracking'),
  woEdit:                     () => import('@pages/production/WOEdit'),
  qcDashboard:                () => import('@pages/quality/QCDashboard'),

  // ═══════════ 工程部 (v329 · Sprint 8-C) ═══════════
  bomTreeEditor:              () => import('@pages/engineering/BomTreeEditor'),
  processManage:              () => import('@pages/engineering/ProcessManage'),
  kitBomManage:               () => import('@pages/engineering/KitBomManage'),
  altMaterialList:            () => import('@pages/engineering/AltMaterialList'),
  corrugatedRecipeList:       () => import('@pages/engineering/CorrugatedRecipeList'),
  artworkReviewQueue:         () => import('@pages/engineering/ArtworkReviewQueue'),
  cadLibrary:                 () => import('@pages/engineering/CadLibrary'),
  cadUpload:                  () => import('@pages/engineering/CadUpload'),
  productCreate:              () => import('@pages/engineering/ProductCreate'),         // ⭐ v349 新
  orderMaterials:             () => import('@pages/engineering/OrderMaterials'),        // ⭐ v350 算料引擎
  markCodeManage:             () => import('@pages/engineering/MarkCodeManage'),        // ⭐ v350 唛头管理

  // ═══════════ 采购部 (v349 新) ═══════════
  supplierCreate:             () => import('@pages/purchasing/SupplierCreate'),         // ⭐ v349 新
  rfqList:                    () => import('@pages/purchasing/RFQList'),                // ⭐ v349 新
  purchasingReport:           () => import('@pages/purchasing/PurchasingReport'),       // ⭐ v349 新

  // ═══════════ 维修部 (v327 · Sprint 8-J) ═══════════
  maintenanceList:            () => import('@pages/maintenance/MaintenanceList'),
  maintenanceRequest:         () => import('@pages/maintenance/MaintenanceRequest'),
  sparePartsManage:           () => import('@pages/maintenance/SparePartsManage'),
  maintenancePlans:           () => import('@pages/maintenance/MaintenancePlans'),      // ⭐ v349 新
  maintenanceSchedule:        () => import('@pages/maintenance/MaintenanceSchedule'),   // ⭐ v349 新
  tpmCenter:                  () => import('@pages/maintenance/TpmCenter'),             // ⭐ v350 TPM 8 大支柱
  equipmentByDept:            () => import('@pages/maintenance/EquipmentByDept'),       // ⭐ v352 真实 34 机型 9 部门
  // equipmentList 已存在 (production/EquipmentList) · v327 不动 · 维修部也可见

  // ═══════════ 跨部门报表 ═══════════
  [Routes.SYSTEM_HEALTH]:     () => import('@pages/reports/SystemHealth'),

  // ═══════════ 系统 (通知/帮助/Hand-off) ═══════════
  [Routes.NOTIFICATIONS]:     () => import('@pages/system/Notifications'),

  // ═══════════ 客诉 + 退货 (Sprint 8-F · v323) ═══════════
  [Routes.COMPLAINTS_CENTER]: () => import('@pages/quality/ComplaintsCenter'),
  [Routes.COMPLAINT_CREATE]:  () => import('@pages/quality/ComplaintCreate'),
  [Routes.RETURNS_LIST]:      () => import('@pages/quality/ReturnsList'),
  [Routes.RETURN_CREATE]:     () => import('@pages/quality/ReturnCreate'),

  // ═══════════ 工具 ═══════════
  [Routes.DATA_MIGRATION]:    () => import('@pages/tools/DataMigration'),

  // ⚠️ 瘦身清单删除 (无注册即 placeholder · 引导 redirect 到部门 Home):
  //   helpCenter / aiAssistant / flowOverview / fieldAclManage
  //   (按 docs/v310-瘦身清单-花瓶功能识别.md 28 项删除)
};

const DEFAULT_HASH = Routes.LOGIN;

// ============================================================
// 公开 API
// ============================================================

export async function initRouter(): Promise<void> {
  window.addEventListener('hashchange', handleRoute);
  document.addEventListener('tn-user-login', () => {
    const user = getCurrentUser();
    if (user) {
      // ⭐ 使用 getHomeByUserCode · 仓储子角色智能跳转
      const home = getHomeByUserCode(user.code, user.role as any);
      window.location.hash = `#${home}`;
    }
  });
  await handleRoute();
}

export function navigate(hash: string): void {
  window.location.hash = `#${hash}`;
}

// ============================================================
// 内部
// ============================================================

async function handleRoute(): Promise<void> {
  const raw = window.location.hash.replace(/^#/, '').split('?')[0] || DEFAULT_HASH;
  const user = getCurrentUser();

  // 1. 未登录 → 强制 login
  if (!user && raw !== Routes.LOGIN) {
    window.location.hash = '#' + Routes.LOGIN;
    return;
  }

  // 2. 已登录访问 login → 跳 home (含仓储子角色智能)
  if (user && raw === Routes.LOGIN) {
    const home = getHomeByUserCode(user.code, user.role as any);
    window.location.hash = `#${home}`;
    return;
  }

  // 3. RBAC 检查
  if (user && !canAccess(user, raw)) {
    console.warn('[router] access denied:', raw, 'for role', user.role);
    const home = getHomeByUserCode(user.code, user.role as any);
    if (raw !== home) {
      window.location.hash = `#${home}`;
      return;
    }
  }

  // 4. 渲染 (lazy load page module)
  const loader = routes[raw];
  const app = document.getElementById('app');
  if (!app) return;

  if (!loader) {
    renderNotFound(app, raw);
    return;
  }

  try {
    app.innerHTML = '<div class="p-8 text-gray-500">加载中... / Đang tải...</div>';

    // v355.1 · chunk fail 2 次 retry + 强 reload (stale CDN/SW · 复用 v200 v153.37 思路)
    const module = await loadModuleWithRetry(loader, raw);
    const content = await module.render();

    app.innerHTML = '';
    if (user) {
      const layout = document.createElement('div');
      layout.className = 'min-h-screen flex flex-col';
      layout.appendChild(renderTopNav(user));
      const body = document.createElement('div');
      body.className = 'flex flex-1';
      body.appendChild(renderSidebar(user, raw));
      const main = document.createElement('main');
      main.className = 'flex-1 p-4 md:p-6 overflow-y-auto';
      main.appendChild(content);
      body.appendChild(main);
      layout.appendChild(body);
      app.appendChild(layout);
    } else {
      app.appendChild(content);
    }

    document.dispatchEvent(new CustomEvent('tn-render-done', { detail: { page: raw } }));
  } catch (err) {
    console.error('[router] render error:', err);
    renderError(app, err);
  }
}

/**
 * v355.1 · chunk fail retry 机制
 * 浏览器拿新 index.js 但 fetch chunk 失败 (Cloudflare propagation race / SW cache / 弱网)
 * - 第 1 次失败: 等 600ms 重试
 * - 第 2 次失败: 强制 reload (绕 SW · 拿最新 index.js + chunks)
 * - 第 3 次失败: 抛错 (用户已 reload 仍失败 · 真问题)
 */
async function loadModuleWithRetry(loader: () => Promise<{ render: PageRenderer }>, page: string): Promise<{ render: PageRenderer }> {
  const RELOAD_KEY = 'tn_chunk_reloaded';
  try {
    return await loader();
  } catch (err1) {
    const msg1 = String(err1);
    if (!msg1.includes('dynamically imported module') && !msg1.includes('Failed to fetch')) throw err1;
    console.warn('[router] chunk fail, retry in 600ms:', page, msg1);
    await new Promise(r => setTimeout(r, 600));
    try {
      return await loader();
    } catch (err2) {
      const msg2 = String(err2);
      if (!msg2.includes('dynamically imported module') && !msg2.includes('Failed to fetch')) throw err2;
      // 强 reload 一次 (sessionStorage 防死循环)
      if (!sessionStorage.getItem(RELOAD_KEY)) {
        sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
        console.warn('[router] chunk fail × 2 · 强制 reload 绕 SW/CDN');
        // 加版本 query 强失效
        const url = new URL(location.href);
        url.searchParams.set('_v', String(Date.now()));
        location.replace(url.toString());
        // 上面 replace 会触发 navigation · 返回未 resolve Promise 防 caller 继续
        return new Promise(() => {});
      }
      throw err2;
    }
  }
}

function renderNotFound(app: HTMLElement, raw: string): void {
  const user = getCurrentUser();
  const home = user ? getHomeByUserCode(user.code, user.role as any) : Routes.LOGIN;
  app.innerHTML = `
    <div class="p-8 max-w-md mx-auto text-center mt-20">
      <div class="text-6xl mb-4">🚧</div>
      <h1 class="text-xl font-bold mb-2">页面建设中</h1>
      <p class="text-gray-600 mb-4">Trang đang xây dựng · ${escapeHtml(raw)}</p>
      <button onclick="window.location.hash='#${home}'" class="px-4 py-2 bg-blue-600 text-white rounded">返回首页</button>
    </div>
  `;
}

function renderError(app: HTMLElement, err: unknown): void {
  app.innerHTML = `
    <div class="p-8 max-w-md mx-auto text-center mt-20">
      <div class="text-6xl mb-4">⚠️</div>
      <h1 class="text-xl font-bold mb-2 text-red-600">加载失败</h1>
      <p class="text-gray-600 mb-4">${escapeHtml(String(err))}</p>
      <button onclick="location.reload()" class="px-4 py-2 bg-blue-600 text-white rounded">刷新页面</button>
    </div>
  `;
}

function escapeHtml(s: string): string {
  return s.replace(/[<>&"']/g, (c) => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;', "'": '&#39;' }[c]!));
}
